import React, { useState } from "react";
import { TextField, Button, Typography, Box, Link } from "@mui/material";
import {
  signUp,
  signIn,
  getCurrentUser,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
  resendSignUpCode,
} from "@aws-amplify/auth";
import Header from "./Header";

const SignIn = ({ onSignInSuccess }) => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    isSignUp: false,
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [isVerificationStep, setIsVerificationStep] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isForgotPassword, setIsForgotPassword] = useState(false); // Track forgot password state
  const [newPassword, setNewPassword] = useState(""); // New password for reset
  const [resetEmail, setResetEmail] = useState(""); // Email for password reset
  const [showLanguageModal, setShowLanguageModal] = useState(false);

  const handleResendCode = async () => {
    const { email } = formData;

    if (!email) {
      setErrorMessage("Please enter your email address first.");
      return;
    }

    setIsSubmitting(true);
    try {
      await resendSignUpCode({ username: email });
      setErrorMessage("Verification code resent. Please check your inbox.");
    } catch (error) {
      setErrorMessage(error.message || "Failed to resend code. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleAuth = async () => {
    const { email, password, isSignUp } = formData;
    setIsSubmitting(true);
    setErrorMessage("");

    try {
      if (isSignUp) {
        const signUpResponse = await signUp({
          username: email,
          password,
          options: { userAttributes: { email } },
        });
        setIsVerificationStep(true);
      } else {
        const signInResponse = await signIn({ username: email, password });

        const user = await getCurrentUser();
        onSignInSuccess();
      }
    } catch (error) {
      // 🚨 NEW: If user is unconfirmed, show verification screen
      if (error.name === "UserNotConfirmedException") {
        setIsVerificationStep(true);
        setErrorMessage("Your account isn't verified yet. Please enter the code sent to your email.");
      } else {
        setErrorMessage(error.message || "Login failed. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerification = async () => {
    const { email, password } = formData;
    setIsSubmitting(true);
    try {
      // Confirm the sign-up with verification code
      await confirmSignUp({ username: email, confirmationCode: verificationCode });
      // Sign the user in
      const signInResponse = await signIn({ username: email, password });
      // Get current user and proceed
      const user = await getCurrentUser();
      setErrorMessage("");
      onSignInSuccess();
    } catch (error) {
      setErrorMessage(error.message || "Verification failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!formData.email) {
      setErrorMessage("Please enter your email address to reset your password.");
      return;
    }

    setIsSubmitting(true);
    try {
      await resetPassword({ username: formData.email });
      setIsForgotPassword(true); // Move to reset code and new password step
      setResetEmail(formData.email); // Store the email for the reset process
      setErrorMessage(""); // Clear any previous error messages
    } catch (error) {
      setErrorMessage(error.message || "Failed to send reset code. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    setIsSubmitting(true);
    try {
      await confirmResetPassword({
        username: resetEmail,
        confirmationCode: verificationCode,
        newPassword: newPassword,
      });
      setErrorMessage("Password reset successful! You can now sign in.");
      setIsForgotPassword(false); // Return to login form
    } catch (error) {
      setErrorMessage(error.message || "Password reset failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        minWidth: "100vw",
        width: "100vw",
        backgroundColor: "#f5f5f5",
        display: "flex",
        flexDirection: "column",
        margin: 0,
        padding: 0,
      }}
    >
      {/* Full-Width White Header */}
      <Header
        onLanguageClick={() => setShowLanguageModal(true)}
        languageLabel="English"
      />

      {/* Centered Login/Signup Form */}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          width: "100%",
          padding: "24px",
          margin: 0,
        }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            padding: "32px",
            borderRadius: "8px",
            boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            maxWidth: "500px",
            width: "100%",
            textAlign: "center",
          }}
        >
          <Typography variant="h5" gutterBottom>
            {formData.isSignUp ? "Create your account" : "Log in to your account"}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {formData.isSignUp ? "Welcome! Please enter your details." : "Welcome back! Please enter your details."}
          </Typography>

          <Box
            component="form"
            onSubmit={async (e) => {
              e.preventDefault();
              if (isSubmitting) return;

              if (isVerificationStep) {
                await handleVerification();
              } else if (isForgotPassword) {
                await handleResetPassword();
              } else {
                await handleAuth();
              }
            }}
            sx={{ mt: 3 }}
          >
            {isVerificationStep ? (
              <>
                <TextField
                  fullWidth
                  label="Verification Code"
                  variant="outlined"
                  name="verificationCode"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={isSubmitting}
                >
                  Verify
                </Button>
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  sx={{ display: "block", mt: 2 }}
                  onClick={handleResendCode}
                >
                  Didn't get a code? Resend verification code
                </Link>
              </>
            ) : isForgotPassword ? (
              <>
                <TextField
                  fullWidth
                  label="Reset Code"
                  variant="outlined"
                  name="verificationCode"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="New Password"
                  variant="outlined"
                  type="password"
                  name="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  sx={{ mb: 2 }}
                />
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={isSubmitting}
                >
                  Reset Password
                </Button>
              </>
            ) : (
              <>
                <TextField
                  fullWidth
                  label="Email"
                  variant="outlined"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  sx={{ mb: 2 }}
                />
                <TextField
                  fullWidth
                  label="Password"
                  variant="outlined"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  sx={{ mb: 2 }}
                />
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  sx={{ display: "block", mb: 1 }}
                  onClick={handleForgotPassword}
                >
                  Forgot password?
                </Link>

                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  sx={{ display: "block", mb: 2 }}
                  onClick={() => {
                    const { email, password } = formData;
                    if (!email || !password) {
                      setErrorMessage("Please enter both your email and password first.");
                      return;
                    }
                    setIsVerificationStep(true);
                    setErrorMessage("Please enter the verification code we sent to your email.");
                  }}
                >
                  Need to verify your account?
                </Link>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={isSubmitting}
                >
                  {formData.isSignUp ? "Sign Up" : "Sign In"}
                </Button>
              </>
            )}
            {errorMessage && (
              <Typography color="error" sx={{ mt: 2 }}>
                {errorMessage}
              </Typography>
            )}
            <Link
              component="button"
              type="button"
              variant="body2"
              sx={{ display: "block", mt: 2 }}
              onClick={() => setFormData({ ...formData, isSignUp: !formData.isSignUp })}
            >
              {formData.isSignUp ? "Already have an account? Log in here." : "Need an account? Sign up here."}
            </Link>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default SignIn;
